import { useMemo, useState } from "react";
import { z } from "zod";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { E164Number } from "libphonenumber-js";

import { PhoneInput, phoneSchema } from "@/components/popadvise/reservations/phone-input";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Reservation, ReservationFormProps } from "@/types/popadvise";
import { FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { parsePhoneNumber } from "@/lib/client-utils";
import { SYSTEM_BE } from "@/lib/constants";
import { toast } from "@/hooks/use-toast";
import { CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export function ReservationUserInfo({ onContinue, onBackStep, userData, setUserData, setResult }: {
    onContinue: () => void
    onBackStep: () => void
    userData: ReservationFormProps
    setUserData: (userData: ReservationFormProps) => void
    setResult: (result: Reservation | null) => void
}) {

    const [userExists, setUserExists] = useState(true)
    const [loading, setLoading] = useState(false)

    const formSchema = useMemo(() => {
        return userExists
            ? z.object({
                name: z.string().optional().nullable(),
                lastName: z.string().optional().nullable(),
                email: z.string().optional().nullable(),
            }).merge(phoneSchema)
            : z.object({
                name: z.string().min(2, { message: "El nombre es requerido." }),
                lastName: z.string().min(2, { message: "El apellido es requerido." }),
                email: z.string().email({ message: "Correo electrónico inválido." }),
            }).merge(phoneSchema)
    }, [userExists]);

    type FormSchemaType = z.infer<typeof formSchema>

    const form = useForm<FormSchemaType>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: "",
            lastName: "",
            email: "",
            phone: userData.phone,
            countryCode: "MX",
        }
    })

    const handleOnSubmit = async (data: FormSchemaType) => {

        if (userExists) {
            const phone = parsePhoneNumber(data.phone)
            const formData = new FormData()
            formData.append("username", phone)
            setLoading(true)
            try {
                const response = await fetch(SYSTEM_BE + "/v1/users/check-client", {
                    method: "POST",
                    body: formData
                })
                if (!response.ok) {
                    setUserExists(false)
                    setLoading(false)
                    return
                } else {
                    setLoading(true)
                    setUserData({
                        ...userData,
                        name: data.name,
                        lastName: data.lastName,
                        email: data.email,
                        phone: data.phone,
                    })

                    const formData = new FormData()
                    formData.append("phone", phone)
                    formData.append("origin", "web")
                    formData.append("reserved_places", userData.persons)
                    formData.append("is_vip", "0")
                    formData.append("date", userData.date.split("T")[0])
                    formData.append("time", userData.hour.replace(":", ""))
                    formData.append("special_request", userData.specialRequest)
                    formData.append("occasion_type", userData.occasion.toString())
                    formData.append("branch_id", userData.branch)

                    fetch(SYSTEM_BE + "/v1/reservations/client", {
                        method: "POST",
                        body: formData
                    })
                        .then(response => {
                            if (!response.ok) {
                                throw new Error("No se pudo crear la reserva")
                            }
                            return response.json()
                        })
                        .then((data) => {
                            setResult(data)
                            onContinue()
                        })
                        .catch(() => {
                            toast({
                                title: "Error",
                                description: "Ocurrió un error al intentar crear la reserva",
                                variant: "destructive"
                            })
                        })
                        .finally(() => {
                            setLoading(false)
                        })
                }
            } catch {
                toast({ title: "Error", description: "Ocurrió un error al intentar verificar el número de teléfono.", variant: "destructive" })
                setLoading(false)
            }
        }

        if (!userExists) {
            setUserData({
                ...userData,
                name: data.name,
                lastName: data.lastName,
                email: data.email,
                phone: parsePhoneNumber(data.phone),
                countryCode: "MX",
            })

            setLoading(true)
            const formData = new FormData()
            formData.append("origin", "web")
            formData.append("client_name", data.name)
            formData.append("client_lastname", data.lastName)
            formData.append("client_email", data.email)
            formData.append("client_phone", parsePhoneNumber(data.phone))
            formData.append("client_country_code", data.countryCode)
            formData.append("client_has_whatsapp", "1")
            formData.append("reserved_places", userData.persons)
            formData.append("is_vip", "0")
            formData.append("date", userData.date.split("T")[0])
            formData.append("time", userData.hour.replace(":", ""))
            formData.append("special_request", userData.specialRequest)
            formData.append("occasion_type", userData.occasion.toString())
            formData.append("branch_id", userData.branch)

            fetch(SYSTEM_BE + "/v1/reservations", {
                method: "POST",
                body: formData
            })
                .then(response => {
                    if (!response.ok) {
                        throw new Error("No se pudo crear la reserva")
                    }
                    return response.json()
                })
                .then(data => {
                    setResult(data)
                    onContinue()
                })
                .catch(() => {
                    toast({
                        title: "Error",
                        description: "Ocurrió un error al intentar crear la reserva",
                        variant: "destructive"
                    })
                })
                .finally(() => {
                    setLoading(false)
                })
        }
    }

    return (
        <div className="p-6 pt-8">
            <CardHeader className="px-0">
                <CardTitle className="text-left text-xl">Ingresa tu teléfono</CardTitle>
                <CardDescription className="text-[#6C6C6C] text-left pr-6">
                    Ingresa número de teléfono para continuar.
                </CardDescription>
            </CardHeader>
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(handleOnSubmit)}>

                    <div>
                        {
                            !userExists && (
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <FormField
                                        control={form.control}
                                        name="name"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <Input className="p-4 py-6" placeholder="Tu nombre" {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name="lastName"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <Input className="p-4 py-6" placeholder="Tu apellido" {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <div className="col-span-2">
                                        <FormField
                                            control={form.control}
                                            name="email"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormControl>
                                                        <Input className="p-4 py-6 grid" placeholder="youremail@example.com" {...field} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                </div>
                            )
                        }
                        <FormField
                            control={form.control}
                            name="phone"
                            render={({ field }) => (
                                <FormItem className="col-span-2 md:col-span-3 flex flex-col gap-1">
                                    <FormControl>
                                        <PhoneInput
                                            className=""
                                            defaultCountry="MX"
                                            value={field.value as E164Number}
                                            onChange={field.onChange}
                                            onCountryChange={(country) => form.setValue("countryCode", country)}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />


                    </div>
                    <footer className="grid grid-cols-1 gap-3 mt-5">
                        <Button
                            type="submit"
                            className={`
                            py-6 rounded-lg w-full font-semibold
                            ${false && 'bg-[#F2F4F7] text-[#A0A9B4]'}    
                        `}
                            loading={loading}
                            disabled={loading}
                        >Continuar</Button>
                        <Button
                            type="button"
                            className="py-6 rounded-lg w-full font-semibold m-0 text-[#6C6C6C]"
                            onClick={() => onBackStep()}
                            variant="ghost"
                        >Atras</Button>
                    </footer>
                </form>
            </FormProvider>
        </div>
    )
}